import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'branch_token';
const BRANCH_KEY = 'branch_profile';
// Feb 2026 — Desk-Tracker scoped token. Kept in its own storage key
// so a "sign in for Desk Tracker only" session cannot clobber a
// pre-existing full-scope branch session. `deskAuthHeaders` below
// prefers this token when it's present; the standard `authHeaders`
// keeps working with the full branch / admin bypass token.
const DESK_TOKEN_KEY = 'branch_desk_token';
const DESK_BRANCH_KEY = 'branch_desk_profile';
// Super-admin bypass (2026-07-19): if an admin JWT is present in
// localStorage under the shared admin key, we treat the caller as an
// authenticated branch user in super-admin mode. The bypass never
// touches the real branch token — logout still preserves the admin
// session, and normal branch logins keep working exactly as before.
const ADMIN_TOKEN_KEY = 'mr_admin_token';

const BranchAuthContext = createContext(null);

export const useBranchAuth = () => useContext(BranchAuthContext);
export { API };

/**
 * BranchAuthProvider — token + branch-profile state for the /branch
 * portal. Mirrors the shape/patterns of AdminAuthContext so any
 * existing conventions (authHeaders, applyToken, logout) transfer
 * over 1:1 for reviewers.
 */
export const BranchAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  // Feb 2026 — Desk Tracker-scoped session state.
  const [deskToken, setDeskToken] = useState(() => localStorage.getItem(DESK_TOKEN_KEY) || '');
  const [deskBranch, setDeskBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DESK_BRANCH_KEY) || 'null'); } catch { return null; }
  });
  const [branch, setBranch] = useState(() => {
    try { return JSON.parse(localStorage.getItem(BRANCH_KEY) || 'null'); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Prefer the real branch token; fall back to the admin token when no
  // branch token is present. This is the one place the bypass is wired.
  const activeToken = token || adminToken;
  const isSuperBypass = !token && !!adminToken;
  const authHeaders = activeToken ? { Authorization: `Bearer ${activeToken}` } : {};

  // Feb 2026 — Desk-scoped headers. Any caller that mounts the Desk
  // Tracker page should use this instead of `authHeaders` so a
  // dedicated desk-only session actually reaches `/branch/desk/*`.
  // Falls back to the full-scope token so a user who's already
  // signed in via the normal branch portal doesn't get re-prompted.
  const deskActiveToken = deskToken || activeToken;
  const deskAuthHeaders = deskActiveToken
    ? { Authorization: `Bearer ${deskActiveToken}` }
    : {};

  const applyToken = useCallback((newToken, branchDoc) => {
    setToken(newToken);
    localStorage.setItem(TOKEN_KEY, newToken);
    if (branchDoc) {
      setBranch(branchDoc);
      localStorage.setItem(BRANCH_KEY, JSON.stringify(branchDoc));
    }
  }, []);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/branch/login`, { username, password });
      applyToken(data.access_token, data.branch);
      // Feb 2026 — Stash attendance payload for BranchAttendanceDialog
      // when the backend fires the first-of-the-day greeting.
      if (data.attendance && data.attendance.show) {
        try { sessionStorage.setItem('mr_branch_attendance', JSON.stringify(data.attendance)); } catch { /* quota */ }
      }
      return { ok: true, mustChangePassword: !!data.must_change_password };
    } catch (err) {
      // The off-duty gate returns `detail` as an object; flatten it
      // into a readable string. Password errors keep the raw string.
      const detail = err?.response?.data?.detail;
      let error;
      let offDuty = null;
      if (detail && typeof detail === 'object') {
        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const daysStr = Array.isArray(detail.duty_days) && detail.duty_days.length > 0
          ? detail.duty_days.map((d) => days[d]).join(', ')
          : 'every day';
        const win = (detail.duty_start && detail.duty_end)
          ? `${detail.duty_start}–${detail.duty_end}`
          : 'as configured';
        error = `${detail.message || 'Off-duty'} · Shift: ${win} on ${daysStr} (Muscat UTC+4).`;
        // Feb 2026 — Surface the structured off-duty payload so the
        // login screen can render a persistent inline banner (title
        // + shift window) instead of a fleeting toast.
        if (detail.code === 'off_duty') {
          offDuty = {
            code: 'off_duty',
            reason: detail.reason || 'off_hours',
            message: detail.message || 'You are currently off-duty.',
            duty_start: (detail.duty_start || '').trim(),
            duty_end: (detail.duty_end || '').trim(),
            duty_days: Array.isArray(detail.duty_days) ? detail.duty_days : [],
            off_duty_days: Array.isArray(detail.off_duty_days) ? detail.off_duty_days : [],
            shift_window_display: win,
            duty_days_display: daysStr,
          };
        }
      } else {
        error = detail || err?.message;
      }
      return { ok: false, error, offDuty };
    } finally {
      setLoading(false);
    }
  }, [applyToken]);

  const logout = useCallback(() => {
    setToken('');
    setBranch(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(BRANCH_KEY);
    // Deliberately DO NOT touch the admin token here — the super admin
    // is still signed in on the admin side and should not be signed
    // out of the admin portal just because they left /branch.
  }, []);

  // Feb 2026 — Desk Tracker sign-in. Hits `/branch/desk-tracker/login`
  // which skips the duty-window gate and returns a token with
  // `scope="desk"`. Stored under its own key so it can't clobber a
  // real branch session.
  const deskLogin = useCallback(async (username, password) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/branch/desk-tracker/login`, {
        username, password,
      });
      setDeskToken(data.access_token);
      localStorage.setItem(DESK_TOKEN_KEY, data.access_token);
      if (data.branch) {
        setDeskBranch(data.branch);
        localStorage.setItem(DESK_BRANCH_KEY, JSON.stringify(data.branch));
      }
      return { ok: true };
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const error = (typeof detail === 'string' && detail) || err?.message || 'Sign-in failed';
      return { ok: false, error };
    } finally {
      setLoading(false);
    }
  }, []);

  const deskLogout = useCallback(() => {
    setDeskToken('');
    setDeskBranch(null);
    localStorage.removeItem(DESK_TOKEN_KEY);
    localStorage.removeItem(DESK_BRANCH_KEY);
  }, []);

  // Watch localStorage across tabs so if the admin logs in / out
  // elsewhere the super-admin bypass reflects that.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ADMIN_TOKEN_KEY) setAdminToken(e.newValue || '');
      if (e.key === TOKEN_KEY) setToken(e.newValue || '');
      if (e.key === DESK_TOKEN_KEY) setDeskToken(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Refresh profile whenever the active token changes so `active=false`
  // deactivations flush the local state on next mount. In super-admin
  // bypass mode `/branch/me` returns the synthetic super-admin dict.
  useEffect(() => {
    if (!activeToken) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/branch/me`, { headers: { Authorization: `Bearer ${activeToken}` } });
        // `/branch/me` returns `{ branch, is_super_admin, on_duty, ... }`
        // — the branch profile lives under `.branch`. Older code
        // dropped the whole wrapper into state, wiping `warehouse_id`
        // and `has_receiving` on every refresh. Guard with a fallback
        // for legacy callers that still return the profile at the
        // root (backwards-compat only).
        const nextBranch = (data && typeof data === 'object' && data.branch) ? data.branch : data;
        setBranch(nextBranch);
        // Only persist to localStorage when we're on the real branch
        // token — persisting the super-admin profile would confuse a
        // future real branch login on the same browser.
        if (!isSuperBypass) {
          localStorage.setItem(BRANCH_KEY, JSON.stringify(nextBranch));
        }
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          logout();
        }
      }
    })();
  }, [activeToken, isSuperBypass, logout]);

  return (
    <BranchAuthContext.Provider value={{
      token: activeToken,
      branch,
      loading,
      authHeaders,
      login,
      logout,
      applyToken,
      isSuperBypass,
      // Feb 2026 — Desk Tracker session slice.
      deskToken: deskActiveToken,
      deskBranch: deskBranch || branch,
      deskAuthHeaders,
      deskLogin,
      deskLogout,
    }}>
      {children}
    </BranchAuthContext.Provider>
  );
};
